import {
  ArrowRightIcon,
  CircleQuestionMarkIcon,
  LockOpenIcon,
  MessageCircleIcon,
  MessageCircleQuestionMarkIcon,
  ShieldQuestionMarkIcon,
  SmileIcon,
  TrophyIcon,
  TvIcon,
} from 'lucide-preact';
import { FunctionComponent, JSX } from 'preact';

import { formatTimeAgoOrTimestamp } from '@/helpers';
import route from '@/lib/route';
import { BellNotificationType } from '@/queries/notifications/types';
import { trackMP } from '@/shared/mp';

interface NotificationProps {
  id: string;
  title: string;
  description: string;
  time: string;
  media?: string;
  viewed: boolean;
  type: BellNotificationType;
  actionURL: string;
  onClick: (id: string) => void;
}

const Notification: FunctionComponent<NotificationProps> = ({
  id,
  title,
  description,
  time,
  media,
  viewed,
  type,
  actionURL,
  onClick,
}) => {
  const header =
    type === 'Album'
      ? 'New comment'
      : type === 'Q&A'
      ? 'Q&A response'
      : type === 'UnlockHabitat'
      ? 'Unlocked habitat'
      : type === 'Comment' || type === 'MediaComment' || type === 'QuestionComment'
      ? 'New reply'
      : title;

  const Icon =
    type === 'Album' || type === 'MediaComment' || type === 'Comment'
      ? MessageCircleIcon
      : type === 'Q&A' || type === 'QuestionComment'
      ? MessageCircleQuestionMarkIcon
      : type === 'Question'
      ? CircleQuestionMarkIcon
      : type === 'UnlockHabitat'
      ? LockOpenIcon
      : type === 'Reaction'
      ? SmileIcon
      : type === 'Recap'
      ? TvIcon
      : type === 'AchievementUnlocked'
      ? TrophyIcon
      : ShieldQuestionMarkIcon;

  const onClickHandler: JSX.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    onClick(id);
    trackMP('bell-notif-click', {
      notificationId: id,
      title,
      description,
      media,
      viewed,
      type,
      actionURL,
    });

    route(actionURL, false, true);
  };

  return (
    <a
      href={actionURL}
      className="preflight preflight-a flex rounded-xl px-2 py-3 hover:bg-[#F7F7F7]"
      onClick={onClickHandler}
    >
      <div className="preflight relative mr-2">
        <div className="preflight flex h-10 w-10 items-center justify-center rounded-full border border-primary-green">
          <span className="sr-only">{type}</span>
          <Icon color="var(--primaryGreen)" />
        </div>

        {!viewed && <i className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-blue-1" />}
      </div>

      <div className="grow">
        <div className="flex items-center justify-between gap-2">
          <h4 className="preflight preflight-h4 text-sm font-semibold">{header}</h4>
          <span className="text-right text-xs text-grey-1 opacity-60">{formatTimeAgoOrTimestamp(new Date(time), true)}</span>
        </div>
        <p className="preflight preflight-p mt-1 text-xs text-grey-6">{description}</p>
        {/* The pinned card's call to action, borrowed: an unlocked achievement is something to go and look at. */}
        {type === 'AchievementUnlocked' && (
          <p className="preflight preflight-p mt-1 flex items-center text-xs">
            <span className="font-semibold text-stone-900 underline">Check it out</span>
            <ArrowRightIcon className="ml-2 inline-block size-4 text-stone-900" />
          </p>
        )}
      </div>
    </a>
  );
};

export default Notification;
